import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseNavigationContext } from "../../Contexts/NavigationContext";
import FooterMenu from "./FooterMenu";
import Socials from "./Socials";

function Footer() {
  const { HandleLinkClick } = UseNavigationContext();

  const [hidden, setHidden] = useState(true);
  const [locked, setLocked] = useState(false);

  const footerTab = useRef(null);

  const remInPixels = useRef(
    parseFloat(getComputedStyle(document.documentElement).fontSize)
  );

  const getFooterHeight = () => remInPixels.current * 4;

  // store computed page-selector bounds in a ref so the pointer handler
  // can read stable values without recreating the handler frequently
  const pageSelectorBounds = useRef({ start: null, end: null });

  const updatePageSelectorBounds = useCallback(() => {
    remInPixels.current = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const footerHeight = getFooterHeight();
    const pageSelectors = document.getElementsByClassName("pageSelector");
    if (pageSelectors.length > 0) {
      const lastRect =
        pageSelectors[pageSelectors.length - 1].getBoundingClientRect();

      if (lastRect.y >= window.innerHeight - footerHeight) {
        pageSelectorBounds.current.start = lastRect.x - remInPixels.current;
        pageSelectorBounds.current.end =
          lastRect.x + lastRect.width + remInPixels.current;
        return;
      }
    }
    pageSelectorBounds.current.start = null;
    pageSelectorBounds.current.end = null;
  }, []);

  function ToggleHidden() {
    setHidden(!hidden);
  }

  function HandleClick() {
    if (hidden) {
      setLocked(true);
    } else {
      setLocked(false);
    }
    ToggleHidden();
  }

  const HandleMouseMove = useCallback(
    (e) => {
      if (locked) {
        return;
      }

      const elementUnderPointer = document.elementFromPoint(
        e.clientX,
        e.clientY
      );
      if (elementUnderPointer?.closest(".disable-footer-hover")) {
        return;
      }

      const { start, end } = pageSelectorBounds.current;
      if (start != null && end != null) {
        if (e.clientX >= start && e.clientX <= end && hidden) {
          return;
        }
      }

      const footerHeight = getFooterHeight();
      if (e.clientY >= window.innerHeight - footerHeight) {
        const tabRectX = footerTab.current?.getBoundingClientRect().x ?? 0;
        if ((hidden && e.clientX < tabRectX - remInPixels.current) || !hidden) {
          setHidden(false);
        }
      } else {
        setHidden(true);
      }
    },
    [locked, hidden]
  );

  useEffect(() => {
    // initial computation
    updatePageSelectorBounds();

    // on resize/scroll, recompute bounds (throttled via rAF)
    const onResizeScroll = () => {
      requestAnimationFrame(updatePageSelectorBounds);
    };

    window.addEventListener("resize", onResizeScroll);
    window.addEventListener("scroll", onResizeScroll, true);

    window.addEventListener("pointermove", HandleMouseMove);

    return () => {
      window.removeEventListener("resize", onResizeScroll);
      window.removeEventListener("scroll", onResizeScroll, true);
      window.removeEventListener("pointermove", HandleMouseMove);
    };
  }, [HandleMouseMove, updatePageSelectorBounds]);

  return (
    <div className="footerContainer">
      <div className="footerTabAnchor">
        <div className="footerTab" onClick={HandleClick} ref={footerTab}>
          <Icon
            icon="ep:arrow-down-bold"
            className={"tabButton" + (hidden ? " hidden" : "")}
          />
          <Icon
            icon="ep:arrow-up-bold"
            className={"tabButton" + (hidden ? "" : " hidden")}
          />
        </div>
      </div>
      <div className={"footer" + (hidden ? " hidden" : "")}>
        <a
          href="/"
          className="copyright"
          onClick={(e) => {
            HandleLinkClick(e, "");
          }}
        >
          © <span className="red">FUNKER</span>
          530
        </a>
        <FooterMenu />
        <Socials />
      </div>
    </div>
  );
}

export default Footer;
